import { Button } from "@circleco/compass/components/Button";
import { useMemo, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { ScopedThemeProvider } from "@/react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription/Offer/ScopedThemeProvider";
import { PaywallCustomization } from "@/react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription/PaywallCustomization";
import { useDetailedOfferContent } from "@/react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription/PaywallCustomization/Detailed/useDetailedOfferContent";
import { usePaywallCustomizationContext } from "@/react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription/PaywallCustomization/PaywallCustomizationProvider";
import { getBestFontColor } from "@/react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription/PaywallCustomization/PriceCallout/getBestFontColor";
import { useButtonActions } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/hooks/useButtonActions";
import { InlineOfferActions } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/InlineOfferActions";
import { OfferActionsLayout } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/OfferActionsLayout";
import { UpgradeOfferActions } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/UpgradeOfferActions";
import { APPEARANCE } from "@/react/providers/Theme";
import type { CheckoutSessionPaywall } from "@/react/types/Paywalls/Checkout/Session";

import { ErrorAlert } from "./ErrorAlert";
import { useUpgradeAcceptance } from "./hooks/useUpgradeAcceptance";
import {
  isUpgradeCouponError,
  useUpgradePreview,
} from "./hooks/useUpgradePreview";
import { UpgradeAcceptedStatus } from "./UpgradeAcceptedStatus";
import { UpgradeDeclineButton } from "./UpgradeDeclineButton";
import {
  UpgradeBannerSkeleton,
  UpgradeSummarySkeleton,
} from "./UpgradeLoadingSkeletons";
import { UpgradeMediaBlocks } from "./UpgradeMediaBlocks";
import { UpgradeOrderDetailsModal } from "./UpgradeOrderDetailsModal";
import { UpgradePlanCards } from "./UpgradePlanCards";
import { UpgradePreviewStatus } from "./UpgradePreviewStatus";
import { UpgradePurchaseBanner } from "./UpgradePurchaseBanner";
const i18nRoot = "paywall_checkout.offer_page.upgrade";
interface UpgradeOfferPageProps {
  checkoutPaywallId: number;
  checkoutSessionId: number;
  communityId: number;
  invoiceId?: string;
  onAcceptanceStateChange: (hasStarted: boolean) => void;
  paymentIntentId?: string;
  paywall: CheckoutSessionPaywall;
  setupIntentId?: string;
  stepId: number;
}
export const UpgradeOfferPage = ({
  checkoutSessionId,
  checkoutPaywallId,
  communityId,
  invoiceId,
  onAcceptanceStateChange,
  paymentIntentId,
  paywall,
  setupIntentId,
  stepId,
}: UpgradeOfferPageProps) => {
  const { paywallCustomization } = usePaywallCustomizationContext();
  const { backgroundColor, cover } = useDetailedOfferContent(paywall);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [hasAcceptanceStarted, setHasAcceptanceStarted] = useState(
    Boolean(invoiceId || paymentIntentId || setupIntentId),
  );
  const orderDetailsButton = useRef<HTMLButtonElement | HTMLAnchorElement>(
    null,
  );
  const { refuseMutation } = useButtonActions({ stepId });
  const visiblePriceIds = paywall.prices.map(price => price.id);
  const {
    continueWithoutCoupon,
    couponFailure,
    couponMode,
    isLoading,
    isRefreshing,
    preview,
    pricePreviews,
    previewError,
    refreshWithoutCoupon,
    retry,
    selectPrice,
    selectedPaywallPriceId,
  } = useUpgradePreview({
    communityId,
    checkoutSessionId,
    paywallId: checkoutPaywallId,
    stepId,
    visiblePriceIds,
    disabled: hasAcceptanceStarted,
  });
  const { accept, isAccepted, isAccepting, isProcessing, targetTrialDays } =
    useUpgradeAcceptance({
      checkoutPaywallId,
      checkoutSessionId,
      couponMode,
      hasPreview: Boolean(preview),
      previewCouponId: preview?.coupon.selected_paywall_coupon_id,
      previewPriceId: preview?.selected_paywall_price_id,
      invoiceId,
      onAcceptanceStateChange: hasStarted => {
        setHasAcceptanceStarted(hasStarted);
        onAcceptanceStateChange(hasStarted);
      },
      onCouponRefreshRequired: refreshWithoutCoupon,
      paymentIntentId,
      selectedPaywallPriceId,
      setupIntentId,
      stepId,
    });
  const errorMessage =
    previewError instanceof Error
      ? previewError.message
      : t([i18nRoot, "preview_error"]);
  const isPreviewLoading = isLoading || (!preview && !previewError);
  const shouldShowPriceSelector =
    paywall.prices.length > 1 ||
    (paywall.prices.length === 1 &&
      selectedPaywallPriceId === undefined &&
      Boolean(previewError));
  const appearance = useMemo(
    () =>
      getBestFontColor(backgroundColor) === "#FFFFFF"
        ? APPEARANCE.DARK
        : APPEARANCE.LIGHT,
    [backgroundColor],
  );
  const hasAuthoredContent =
    (paywallCustomization?.media_block_properties?.enabled &&
      (paywallCustomization.media_block_properties.media_blocks?.length ?? 0) >
        0) ||
    paywallCustomization?.benefit_properties?.enabled ||
    paywallCustomization?.testimonial_properties?.enabled;
  const canOfferCouponRecovery =
    couponMode === "policy" &&
    selectedPaywallPriceId !== undefined &&
    Boolean(couponFailure) &&
    isUpgradeCouponError(previewError);
  return (
    <OfferActionsLayout
      actions={
        preview && (
          <UpgradeOfferActions
            hasAcceptanceStarted={hasAcceptanceStarted}
            isAccepted={isAccepted}
            isAccepting={isAccepting}
            isDeclining={refuseMutation.isPending || refuseMutation.isSuccess}
            isOrderDetailsOpen={isOrderDetailsOpen}
            isProcessing={isProcessing}
            isRefreshing={isRefreshing}
            onAccept={accept}
            onDecline={() => refuseMutation.mutate()}
            onOpenOrderDetails={button => {
              orderDetailsButton.current = button;
              setIsOrderDetailsOpen(true);
            }}
            preview={preview}
          />
        )
      }
    >
      <main className="flex min-h-0 w-full flex-col items-center gap-y-6 pb-6 md:pb-12">
        <ErrorAlert />
        <div className="flex w-full flex-col">
          {!preview && isLoading && <UpgradeBannerSkeleton />}
          {preview && <UpgradePurchaseBanner source={preview.source} />}
          <ScopedThemeProvider appearance={appearance}>
            <section
              className="flex w-full flex-col items-center gap-y-6 px-5 py-6 md:gap-y-8 md:px-10 md:py-12"
              data-testid="upgrade-details-pricing-region"
              style={{ backgroundColor }}
            >
              <PaywallCustomization.Detailed paywall={paywall}>
                <PaywallCustomization.Countdown variant="offer_v2" />
                <PaywallCustomization.Badges />
                {cover}
                {(!hasAcceptanceStarted || preview) && (
                  <UpgradePlanCards
                    isDisabled={hasAcceptanceStarted}
                    isLoading={isLoading}
                    isRefreshing={isRefreshing}
                    paywall={paywall}
                    preview={preview}
                    pricePreviews={pricePreviews}
                    selectedPriceId={
                      selectedPaywallPriceId === undefined
                        ? ""
                        : String(selectedPaywallPriceId)
                    }
                    showPriceSelector={shouldShowPriceSelector}
                    onPriceChange={priceId => {
                      if (hasAcceptanceStarted) return;
                      setIsOrderDetailsOpen(false);
                      selectPrice(priceId);
                    }}
                  />
                )}
                {hasAcceptanceStarted && !preview && (
                  <UpgradeAcceptedStatus
                    isProcessing={isProcessing}
                    targetTrialDays={targetTrialDays}
                  />
                )}
                {!hasAcceptanceStarted && isPreviewLoading && (
                  <UpgradePreviewStatus isLoading />
                )}
                {!hasAcceptanceStarted && Boolean(previewError) && (
                  <div className="w-full max-w-xl space-y-3" role="alert">
                    <UpgradePreviewStatus message={errorMessage} />
                    {canOfferCouponRecovery && (
                      <Button
                        variant="secondary"
                        data-testid="upgrade-continue-without-coupon"
                        onClick={continueWithoutCoupon}
                      >
                        {t([i18nRoot, "continue_without_coupon"])}
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      data-testid="upgrade-retry-preview"
                      onClick={retry}
                      disabled={isPreviewLoading}
                    >
                      {t([i18nRoot, "retry_preview"])}
                    </Button>
                  </div>
                )}
                {!hasAcceptanceStarted &&
                  Boolean(couponFailure) &&
                  couponMode === "without_coupon" && (
                    <UpgradePreviewStatus
                      message={
                        couponFailure instanceof Error
                          ? couponFailure.message
                          : t([i18nRoot, "coupon_preview_failed"])
                      }
                    />
                  )}
                {!hasAcceptanceStarted && !preview && isLoading && (
                  <UpgradeSummarySkeleton />
                )}
                {!hasAcceptanceStarted && !preview && (
                  <div className="w-full max-w-xl">
                    <UpgradeDeclineButton stepId={stepId} />
                  </div>
                )}
                {preview && (
                  <>
                    <div className="w-full max-w-xl">
                      <InlineOfferActions />
                    </div>
                    <UpgradeOrderDetailsModal
                      open={isOrderDetailsOpen}
                      onOpenChange={open => {
                        setIsOrderDetailsOpen(open);
                        if (!open) {
                          requestAnimationFrame(() =>
                            orderDetailsButton.current?.focus({
                              preventScroll: true,
                            }),
                          );
                        }
                      }}
                      preview={preview}
                      onAccept={accept}
                      isAcceptDisabled={isAccepted || isAccepting}
                      isAccepting={isAccepting}
                    />
                  </>
                )}
              </PaywallCustomization.Detailed>
            </section>
          </ScopedThemeProvider>
        </div>
        {hasAuthoredContent && (
          <div className="flex w-full flex-col gap-y-12">
            <UpgradeMediaBlocks customization={paywallCustomization} />
            {paywallCustomization?.benefit_properties?.enabled && (
              <div className="mx-auto w-full px-5 md:max-w-[540px] md:px-0">
                <PaywallCustomization.Benefits />
              </div>
            )}
            {paywallCustomization?.testimonial_properties?.enabled && (
              <div className="mx-auto w-full px-5 md:max-w-[540px] md:px-0">
                <PaywallCustomization.Testimonials />
              </div>
            )}
          </div>
        )}
      </main>
    </OfferActionsLayout>
  );
};
